import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Convention, ConventionRule, pickConvention, withRule, withoutRule } from './convention.model';

/**
 * ConventionDTO con tutti i campi valorizzati: con i soli campi obbligatori dello swagger il backend
 * risponde 400. Numeri vuoti = 0, monte ore condiviso spento se non impostato.
 */
export function toPayload(convention: Convention): Convention {
  const pool = convention.sharedHoursPool;
  return {
    ...(convention.id ? { id: convention.id } : {}),
    companyId: convention.companyId,
    status: convention.status,
    validFrom: convention.validFrom ?? null,
    validTo: convention.validTo ?? null,
    totalHours: convention.totalHours ?? 0,
    sharedHoursPool: {
      enabled: !!pool?.enabled,
      hours: pool?.hours ?? 0,
      businessLines: pool?.businessLines ?? [],
    },
    rules: (convention.rules ?? []).map((rule) => ({
      productId: rule.productId,
      pricingMode: rule.pricingMode,
      minPrice: rule.minPrice ?? 0,
      // A prestazione c'è un solo costo (minPrice); a ore un intervallo, il massimo vuoto vale il minimo.
      maxPrice: rule.pricingMode === 'HOURLY' ? rule.maxPrice ?? rule.minPrice ?? 0 : null,
      quantity: rule.quantity ?? 0,
    })),
  };
}

/** Endpoint `/v1/conventions`: la convenzione dell'azienda, con le regole di prezzo dei prodotti B2B. */
@Injectable({
  providedIn: 'root',
})
export class B2BCompanyConventionService {
  private baseUrl = `${environment.baseURL}/conventions`;

  constructor(private http: HttpClient) {}

  /** La convenzione dell'azienda (la prima attiva), o null se non c'è. */
  getByCompany(companyId: string): Observable<Convention | null> {
    return this.http
      .get<Convention[]>(this.baseUrl, { params: { companyId } })
      .pipe(map((list) => pickConvention(list)));
  }

  /** Crea con POST (senza id) o aggiorna con PUT /{id}. Il corpo è sempre completo, vedi `toPayload`. */
  save(convention: Convention): Observable<Convention> {
    const body = toPayload(convention);
    return convention.id
      ? this.http.put<Convention>(`${this.baseUrl}/${convention.id}`, body)
      : this.http.post<Convention>(this.baseUrl, body);
  }

  delete(id: string) {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  /** Salva la regola di un prodotto nella convenzione esistente (va creata prima, con le date). */
  saveRule(convention: Convention, rule: ConventionRule): Observable<Convention> {
    return this.save(withRule(convention, rule));
  }

  removeRule(convention: Convention, productId: number): Observable<Convention> {
    return this.save(withoutRule(convention, productId));
  }
}
